import { images } from '../images';
import type { Course } from '../../types/content';

const yearRound = ['dubai', 'london'];

export const englishSpecialistCourses: Course[] = [
  {
    slug: 'academic-english',
    name: 'Academic English',
    group: 'english',
    season: 'year-round',
    keyword: 'english for academic purposes course',
    metaTitle: 'Academic English (EAP) Course | Prepare for University | ES World Dubai',
    metaDescription: 'English for Academic Purposes in Dubai: academic writing, critical reading, presentations and lectures. Upper-intermediate level.',
    h1: 'Academic English (EAP) course for university study',
    lead: 'Our English for Academic Purposes course prepares you for study at an English-speaking university. You practise listening to lectures, critical reading, academic writing and presentations, using a more formal register and the vocabulary universities expect.',
    cardText: 'Lectures, academic writing and presentations.',
    audience: 'Students, upper-intermediate',
    image: images.hero,
    facts: [
      { label: 'Course length', value: '12 weeks' },
      { label: 'Entry level', value: 'Upper-intermediate (B2)' },
      { label: 'Progression', value: 'Intermediate to Upper-intermediate' },
      { label: 'Availability', value: 'All year' },
    ],
    valueTitle: 'Study with confidence at university',
    valueText: 'You learn through a mix of teacher-led and student-led activities: discussions, group work, peer evaluation and one-to-one tutor guidance.',
    outcomes: [
      'Write clear academic essays and assignments',
      'Read and analyse academic texts critically',
      'Follow lectures and take useful notes',
      'Give presentations and speak in seminars',
      'Use formal academic vocabulary and register',
      'Prepare for the demands of university life',
    ],
    whoTitle: 'For future university students',
    whoText: 'You need an upper-intermediate level. General English students can join once they reach Upper-Intermediate, or after at least 6 weeks of Intermediate General English.',
    whoFor: [
      { title: 'School leavers', text: 'You plan to start a degree taught in English.' },
      { title: 'Graduate applicants', text: 'You want to study a Master’s course in English.' },
      { title: 'General English students', text: 'You want to move from everyday to academic English.' },
      { title: 'Researchers', text: 'You need to read and write research in English.' },
    ],
    weekTitle: 'Learn the way universities teach',
    weekText: 'Your week mixes lectures, seminars and writing work, like a university timetable. Each week ends with a short project.',
    week: [
      { day: 'Monday', focus: 'Short lecture and note-taking practice' },
      { day: 'Tuesday', focus: 'Critical reading and summary writing' },
      { day: 'Wednesday', focus: 'Seminar discussion on a weekly topic' },
      { day: 'Thursday', focus: 'Essay planning and writing with feedback' },
      { day: 'Friday', focus: 'Presentation and peer evaluation' },
    ],
    faqs: [
      { q: 'How is EAP different from General English?', a: 'EAP uses a more formal, academic register. It focuses on academic vocabulary, critical reading and writing, and clear pronunciation for presentations.' },
      { q: 'How long is the course?', a: 'The course runs for 12 weeks.' },
      { q: 'Can I join from General English?', a: 'Yes — once you reach Upper-Intermediate, or after at least 6 weeks of Intermediate General English.' },
    ],
    ctas: {
      heroPrimary: 'Take the free level test',
      heroSecondary: 'View the university week',
      midPrimary: 'Get my Academic English price',
      midLink: { label: 'Read about our teaching method', to: '/about/teaching-methodology' },
      bottomPrimary: 'Send my study enquiry',
    },
    locations: ['dubai'],
  },
  {
    slug: 'business-english',
    name: 'Business English',
    group: 'english',
    season: 'year-round',
    keyword: 'business english course',
    metaTitle: 'Business English Course | English for Work | ES World',
    metaDescription: 'Business English in Dubai or London: meetings, presentations, negotiations and business writing. Upper-intermediate and above.',
    h1: 'Business English course for your career',
    lead: 'Our Business English course helps you communicate with confidence in the workplace. You practise meetings, presentations and negotiations, and write reports, emails and summaries — using real case studies and the language of today’s industries.',
    cardText: 'Meetings, presentations and business writing.',
    audience: 'Professionals and students, upper-intermediate',
    image: images.teacher,
    facts: [
      { label: 'Course length', value: '1 to 12 weeks' },
      { label: 'Entry level', value: 'Upper-intermediate (B2) and up' },
      { label: 'London timetable', value: 'Mon–Fri, 9:00–12:15' },
      { label: 'Availability', value: 'All year' },
    ],
    valueTitle: 'Speak with confidence at work',
    valueText: 'Lessons are practical and interactive. You learn business vocabulary and expressions, work through real case studies and get feedback on your writing.',
    outcomes: [
      'Lead and take part in meetings',
      'Give clear presentations and handle questions',
      'Negotiate with confidence',
      'Write reports, emails and summaries',
      'Use business vocabulary and expressions naturally',
      'Understand business language and culture',
    ],
    whoTitle: 'For professionals and ambitious students',
    whoText: 'This course suits professionals, students and entrepreneurs who want stronger English for the workplace. You need an upper-intermediate level.',
    whoFor: [
      { title: 'Professionals', text: 'You use English in meetings, calls and emails.' },
      { title: 'Entrepreneurs', text: 'You pitch, negotiate and network in English.' },
      { title: 'Job seekers', text: 'You want to stand out at international companies.' },
      { title: 'Business students', text: 'You are preparing for a career in an international team.' },
    ],
    weekTitle: 'A week of real work tasks',
    weekText: 'Each day has a business topic and a practical task. At the end of the week you take part in a meeting simulation.',
    week: [
      { day: 'Monday', focus: 'Networking and professional small talk' },
      { day: 'Tuesday', focus: 'Emails, reports and business vocabulary' },
      { day: 'Wednesday', focus: 'Case study and team discussion' },
      { day: 'Thursday', focus: 'Presentation skills and negotiation' },
      { day: 'Friday', focus: 'Meeting simulation and feedback' },
    ],
    faqs: [
      { q: 'What topics does the course cover?', a: 'Meetings, presentations, negotiations, networking and business writing such as reports, emails and summaries.' },
      { q: 'Will I practise writing?', a: 'Yes. You write regularly and your teacher gives you feedback on every task.' },
      { q: 'Do I get a certificate?', a: 'Yes. Students who attend regularly receive a certificate of participation.' },
      { q: 'Why study Business English in Dubai?', a: 'With around 85% of Dubai’s population made up of expats, English is the main language of business in the city.' },
    ],
    ctas: {
      heroPrimary: 'Book a free business lesson',
      heroSecondary: 'See the business week',
      midPrimary: 'Get my course price',
      midLink: { label: 'Read our Business English guide', to: '/blog/business-english-course-london' },
      bottomPrimary: 'Send my business enquiry',
    },
    locations: yearRound,
  },
  {
    slug: 'es-flex',
    name: 'ES Flex',
    group: 'english',
    season: 'year-round',
    keyword: 'evening english classes dubai',
    metaTitle: 'ES Flex | Evening English Lessons in Dubai | ES World',
    metaDescription: 'Flexible evening English lessons at our JLT campus in Dubai. Themed speaking sessions, weekday evenings. First session free.',
    h1: 'ES Flex: evening English lessons that fit your week',
    lead: 'ES Flex is a flexible, membership-based programme of evening English lessons at our JLT campus in Dubai. Each themed session stands on its own — presentations, debates, role-plays, negotiations or storytelling — so you can come once a week or every night, and miss a session without falling behind.',
    cardText: 'Themed evening sessions, booked when it suits you.',
    audience: 'Working professionals and Dubai residents',
    image: images.hero,
    facts: [
      { label: 'Time', value: 'Weekdays, 6:30pm–8:00pm' },
      { label: 'Levels', value: 'Intermediate (A2/B1) · Advanced (B2/C1)' },
      { label: 'Group size', value: '15 to 20 per session' },
      { label: 'Location', value: 'JLT campus, Dubai' },
    ],
    valueTitle: 'Speak more, on your own schedule',
    valueText: 'There is no fixed curriculum and nothing to catch up on. A2/B1 sessions use guided practice; B2/C1 sessions are discussion-led, with your teacher as facilitator. Every 10 sessions you get a personal progress review.',
    outcomes: [
      'Hold everyday conversations and small talk with ease',
      'Present and pitch ideas clearly',
      'Debate current affairs and give your opinion',
      'Negotiate and persuade with confidence',
      'Tell stories and speak more fluently',
      'Communicate clearly at work',
    ],
    whoTitle: 'Made for busy people',
    whoText: 'ES Flex suits working professionals and Dubai residents at intermediate to advanced level. You take a placement test before your first session, and you must already hold a valid UAE visa or ID.',
    whoFor: [
      { title: 'Working professionals', text: 'You want to practise after work, not instead of it.' },
      { title: 'Dubai residents', text: 'You live in the city and want regular speaking practice.' },
      { title: 'Intermediate to advanced learners', text: 'You know English and want to use it more.' },
      { title: 'Busy schedules', text: 'Your week changes, so you need to book as you go.' },
    ],
    weekTitle: 'Your evening week',
    weekText: 'Sessions run every weekday evening. Pick the evenings that match your level and book from the weekly menu.',
    week: [
      { day: 'Monday', focus: 'Intermediate (A2/B1) session' },
      { day: 'Tuesday', focus: 'Advanced (B2/C1) session' },
      { day: 'Wednesday', focus: 'Intermediate (A2/B1) session' },
      { day: 'Thursday', focus: 'Advanced (B2/C1) session' },
      { day: 'Friday', focus: 'Intermediate (A2/B1) session' },
    ],
    faqs: [
      { q: 'How do I start?', a: 'Take the placement test, choose a membership, browse the weekly menu of topics and book your sessions. Your first session is free.' },
      { q: 'What if I miss a session?', a: 'No problem. Each session is self-contained, so you never need to catch up.' },
      { q: 'Can ES Flex support a student visa?', a: 'No. ES Flex does not qualify for ES visa sponsorship, and sessions cannot be transferred to other courses or weeks. You need a valid UAE visa or ID to join.' },
      { q: 'How do I track my progress?', a: 'Your teacher gives you a personal review every 10 sessions.' },
    ],
    ctas: {
      heroPrimary: 'Book my free first session',
      heroSecondary: 'See the evening week',
      midPrimary: 'Get ES Flex membership prices',
      midLink: { label: 'Compare with the Speaking Class', to: '/programmes/speaking-class' },
      bottomPrimary: 'Send my ES Flex enquiry',
    },
    locations: ['dubai'],
  },
  {
    slug: 'english-private-lessons',
    name: 'English Private Lessons',
    group: 'english',
    season: 'year-round',
    keyword: 'private english lessons',
    metaTitle: 'Private English Lessons | One-to-One Classes | ES World',
    metaDescription: 'Book private English lessons with a qualified teacher. You choose the topics, times and pace. Book a free trial lesson.',
    h1: 'Private English lessons planned around you',
    lead: 'Our private English lessons give you one teacher and one plan: yours. You choose the topics, the times and the pace. Your teacher prepares every lesson for your goal, such as a job interview, an exam, a presentation or everyday conversation.',
    cardText: 'One-to-one lessons with a plan made for you.',
    audience: 'Adults and teens, 14+',
    image: images.teacher,
    facts: [
      { label: 'Lesson packs', value: '000 lessons' },
      { label: 'Lesson length', value: '000 minutes' },
      { label: 'Levels', value: 'Beginner to advanced' },
      { label: 'Start dates', value: 'Any weekday' },
    ],
    valueTitle: 'Reach your goal faster',
    valueText: 'Your teacher focuses only on you. You spend the whole lesson on the skills and topics that matter for your goal.',
    outcomes: [
      'Work on the exact skills you need',
      'Practise for a real interview, exam or meeting',
      'Get instant feedback on every sentence',
      'Choose lesson times that fit your week',
      'Study at your own pace, slow or fast',
      'Combine private lessons with a group course',
    ],
    whoTitle: 'For learners with a clear goal',
    whoText: 'Private lessons suit anyone who wants a personal plan. You can take them alone or add them to a group course.',
    whoFor: [
      { title: 'Professionals', text: 'You need English for one important task at work.' },
      { title: 'Exam candidates', text: 'You want extra practice for one part of an exam.' },
      { title: 'Shy speakers', text: 'You prefer to practise alone before you speak in a group.' },
      { title: 'Busy people', text: 'You can only study at certain times.' },
    ],
    weekTitle: 'A sample private plan',
    weekText: 'This is one example for a learner who is preparing for a job interview. Your own plan follows your goal.',
    week: [
      { day: 'Monday', focus: 'Goal setting and a speaking level check' },
      { day: 'Tuesday', focus: 'Talking about your experience and skills' },
      { day: 'Wednesday', focus: 'Common interview questions and answers' },
      { day: 'Thursday', focus: 'Pronunciation and clear speaking' },
      { day: 'Friday', focus: 'Full practice interview with feedback' },
    ],
    faqs: [
      { q: 'Can I choose my teacher?', a: 'You can tell us what kind of teacher you prefer. We match you with a teacher who suits your goal.' },
      { q: 'Can I take private lessons online?', a: 'Ask our team. Online private lessons may be available for your plan.' },
    ],
    ctas: {
      heroPrimary: 'Book a free private lesson',
      heroSecondary: 'See a sample plan',
      midPrimary: 'Get private lesson prices',
      midLink: { label: 'Compare with ES Flex', to: '/programmes/es-flex' },
      bottomPrimary: 'Send my private lesson request',
    },
    locations: yearRound,
  },
];
